import { UseQueryOptions } from '@tanstack/react-query';
import { useDataChainId } from '@ui/hooks/datachain';
import { useQueryOnce } from '@ui/hooks/useQueryOnce';
import { toReadableValue } from '@ui/lib/bignumber';
import BigNumber from 'bignumber.js';
import { isNil } from 'lodash-es';
import { useAccount, usePublicClient } from 'wagmi';

import { getTradingPortalFacet } from '../../contract-instances/getTradingPortalFacet';
import { Position } from '../TradingReaderFacet/useGetPositionV2/types';

export const useEstimateCloseTradeGasLimit = <TSelect = BigNumber>({
  positionHash,
  isMergedPosition = false,
  closeQty,
  broker = 0,
  opts,
}: {
  positionHash?: Position['id'];
  isMergedPosition?: boolean;
  closeQty?: bigint;
  broker?: number;
  opts?: Pick<UseQueryOptions<BigNumber, Error, TSelect>, 'enabled'>;
}) => {
  const { address } = useAccount();
  const chainId = useDataChainId();
  const client = usePublicClient({
    chainId,
  });

  return useQueryOnce({
    meta: {
      type: 'estimateGas',
    },
    queryKey: [
      'TradingPortalFacet.estimateGas.closeTrade',
      address,
      chainId,
      client?.uid,
      positionHash,
      isMergedPosition,
      closeQty?.toString(),
      broker,
    ],
    queryFn: async () => {
      if (isNil(positionHash) || isNil(address) || !client) {
        throw new Error('Invalid position hash or address, client');
      }

      const contract = getTradingPortalFacet({
        chainId,
        publicClient: client,
      });
      const args = !isMergedPosition
        ? ([positionHash] as const)
        : closeQty
        ? ([positionHash, closeQty, broker] as const)
        : ([positionHash, broker] as const);
      const result = await contract.estimateGas.closeTrade(args as never, {
        account: address,
      });

      if (isNil(result)) return undefined;

      return toReadableValue(result);
    },
    enabled: !!address && !!positionHash && (opts?.enabled ?? true),
  });
};
